import { useCallback } from 'react';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';

interface Contact {
  id: string;
  name: string;
  channel: 'whatsapp' | 'facebook-messenger' | 'telegram' | 'email' | 'instagram' | 'x-twitter' | 'phone';
  phoneNumber?: string;
  facebookId?: string;
  email?: string;
  phone?: string;
}

interface ConversationHandlerOptions {
  onSuccess?: (conversationId: string, isNew: boolean) => void;
  onError?: (error: string) => void;
}

const isMaskedPhoneValue = (value?: string | null) => !!value && value.includes('*');

export const useConversationHandler = (options?: ConversationHandlerOptions) => {
  const { showNotification, handleConversationNavigation } = useAppContext();
  const { currentSubscription } = useAppStore();

  const startConversation = useCallback(async (contact: Contact, resolvedPhoneNumber?: string) => {
    try {
      console.log('🔍 [useConversationHandler] Starting conversation with contact:', contact);

      // Get phone number ID for WhatsApp conversations
      let phoneNumberId = 'default-phone-number';
      const whatsappPhones = (currentSubscription as any)?.services?.wabu?.metadata?.whatsapp_phone;
      if (contact.channel === 'whatsapp' && Array.isArray(whatsappPhones) && whatsappPhones.length > 0) {
        const phone = whatsappPhones[0];
        phoneNumberId = phone.whatsapp_phone_number_id || phone.phone_id || phone.id || phone.phone_number_id || 'default-phone-number';
      }

      // Prepare contact data for API
      // resolvedPhoneNumber, when provided, wins over contact.phoneNumber/.phone —
      // those fields come from list/search responses which may be MASKED
      // (contacts.viewMobileNumber). Confirmed real production bug: starting a
      // conversation straight off a masked contact object persisted the masked
      // string ("+*******6631" or similar) as the conversation's phone number,
      // eventually surfacing as WhatsApp API error 100 "The parameter to is
      // required" once every message sent in that conversation inherited it.
      // Callers with access to a masked Contact should resolve the real number
      // first (e.g. via apiService.getContactDialTarget, same as the phone
      // dialer's handleCallPress) and pass it here explicitly.
      let phoneNumber = resolvedPhoneNumber || contact.phoneNumber || contact.phone;
      if (contact.channel === 'whatsapp' && (!phoneNumber || isMaskedPhoneValue(phoneNumber))) {
        const hadMaskedPhoneNumber = isMaskedPhoneValue(phoneNumber);
        const target = await apiService.getContactDialTarget(contact.id, 'whatsapp');
        if (target.statusCode === 1000 && target.data?.phoneNumber) {
          phoneNumber = target.data.phoneNumber;
        } else if (hadMaskedPhoneNumber) {
          phoneNumber = undefined;
        }
      }

      const contactData = {
        contactId: contact.id,
        contactName: contact.name,
        phoneNumber,
        channel: contact.channel,
        providerId: `default-${contact.channel}`,
        phoneNumberId: phoneNumberId,
      };

      // Call the API to create or find conversation
      const response = await apiService.createOrFindConversation(contactData);
      
      if (response.statusCode === 1000 && response.data) {
        const { conversationId, isNew } = response.data;
        
        console.log('✅ [useConversationHandler] Conversation handled:', { conversationId, isNew });
        
        // Show success notification
        const message = isNew 
          ? `Started new conversation with ${contact.name}` 
          : `Opened conversation with ${contact.name}`;
        showNotification('success', 'Conversation', message);
        
        // Call success callback if provided
        if (options?.onSuccess) {
          options.onSuccess(conversationId, isNew);
        }
        
        // Navigate to the conversation
        handleConversationNavigation(conversationId);
        
      } else {
        const errorMessage = Array.isArray(response.errors) && response.errors.length > 0 
          ? response.errors[0].message 
          : 'Failed to create or find conversation';
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error('❌ [useConversationHandler] Error starting conversation:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to start conversation';
      
      // Show error notification
      showNotification('error', 'Error', errorMessage);
      
      // Call error callback if provided
      if (options?.onError) {
        options.onError(errorMessage);
      }
    }
  }, [showNotification, handleConversationNavigation, currentSubscription, options]);

  return {
    startConversation,
  };
};
